import { RouteRecordRaw } from 'vue-router';
import { onboardingVariant, deviceVariant } from 'src/utils/onboardingVariant';
import { Parse } from 'src/boot/parse';
import { IS_GUEST_APP } from 'src/utils/appMode';

// App mode (build-time APP_MODE): 'client' (default) or 'admin'. Same routes either way —
// only what a LOGGED-OUT visitor sees first differs, and since 2026-09-27 not even that.
//
// ⭐ BOTH BUILDS NOW OPEN ON THE LANDING PAGE. The admin build used to drop a logged-out
// visitor straight onto /login, because the old landing page was a client-facing thing
// with nothing on it for staff. The rebuilt one carries both doors — "Book a …" and
// "Staff login / Register" — plus the showcase businesses and the demo link, so sending
// staff past it gained nothing and cost them the demo and the examples.
//
// ⚠️ The logged-in shortcut is KEPT for the admin build, deliberately. Someone who has
// already signed in is not landing, they are returning to work: making them tap through a
// welcome screen on every launch would be a daily tax on the people who use the app most.
// The client build has always shown the landing page to everyone and still does — there,
// `goToStaff()` sends a logged-in user straight to their board, so the door is one tap away.
const homeRoute: RouteRecordRaw = process.env.APP_MODE === 'admin'
  ? {
      path: '',
      name: 'home',
      // Logged in → straight to work. Logged out → the same landing page the client gets.
      redirect: (to) =>
        Parse.User.current() ? { name: 'admin' } : { name: 'home.landing', query: to.query },
    }
  : {
      path: '',
      name: 'home',
      component: () => import('pages/planner/LandingPage.vue'),
    };

/**
 * The landing page under its own name, so the admin build's redirect has somewhere to go.
 * ⚠️ Only registered for the admin build: in the client build `home` IS the landing page,
 * and two routes rendering the same component at different paths would split the analytics
 * and give the page two URLs.
 */
const landingRoute: RouteRecordRaw[] = process.env.APP_MODE === 'admin'
  ? [{
      path: 'welcome',
      name: 'home.landing',
      component: () => import('pages/planner/LandingPage.vue'),
    }]
  : [];

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      homeRoute,
      ...landingRoute,
      {
        path: 'select-business',
        name: 'select-business',
        components: {
          default: () => import('pages/planner/IndexPage.vue'),
          header: () => import('layouts/BasicToolbar.vue'),
        },
        meta: {
          clientGrid: true
        }
      },
      {
        // `:prepayOutcome/:holdId` — Grow's deposit return (prepaid | prepay-cancel). Grow
        // refuses '?' and '=' in return URLs, so it cannot use Stripe's ?prepay=done.
        path: 'table-booking/:businessId/:prepayOutcome?/:holdId?',
        name: 'table.booking',
        components: {
          default: () => import('pages/shared/BookingPage.vue'),
          header: () => import('layouts/BasicToolbar.vue'),
        },
        meta: {
          changeBusiness: true,
          clientGrid: true
        }
      },
      {
        path: 'admin',
        name: 'admin',
        components: {
          default: () => import('pages/planner/AdminDashboard.vue'),
        }
      },
      // Help Topics — one row per capability guide on the marketing site.
      // ?topic=<slug> marks the row the reader came from (see HowToChip).
      {
        path: 'admin/help',
        component: () => import('pages/shared/HelpTopicsPage.vue'),
      },
      {
        path: 'admin/profile',
        name: 'profile',
        components: {
          default: () => import('pages/shared/Profile.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        path: 'admin/create',
        name: 'admin.create',
        // Onboarding A/B (src/utils/onboardingVariant.ts): owners on the playground arm are sent
        // to its own page; everyone else stays on the classic wizard, unchanged.
        beforeEnter: (to) => onboardingVariant(to.query.flow) === 'playground'
          ? { name: 'admin.create.playground', query: to.query }
          : true,
        components: {
          default: () => import('pages/shared/CreateBusiness.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        // The playground onboarding — arm B. Reachable directly for testing.
        path: 'admin/create/playground',
        name: 'admin.create.playground',
        components: {
          default: () => import('pages/shared/CreateBusinessPlayground.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        path: 'admin/business/:businessId',
        name: 'admin.business',
        components: {
          default: () => import('pages/shared/AdminMainboard.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        path: 'admin/business/:businessId/hours',
        name: 'admin.business.hours',
        components: {
          default: () => import('pages/shared/HoursSetting.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        path: 'admin/business/:businessId/analytics',
        name: 'admin.business.analyticsdashboard',
        components: {
          default: () => import('pages/shared/AnalyticsDashboard.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        path: 'admin/business/:businessId/history',
        name: 'admin.business.historydashboard',
        components: {
          default: () => import('pages/shared/HistoryDashboard.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        path: 'admin/business/:businessId/tables',
        name: 'admin.business.tables',
        components: {
          default: () => import('pages/shared/Tables.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        path: 'admin/business/:businessId/staff',
        name: 'admin.business.staff',
        components: {
          default: () => import('pages/shared/Staff.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        path: 'admin/business/:businessId/planner-dashboard',
        name: 'admin.planner-dashboard',
        components: {
          default: () => import('pages/planner/PlannerDashboard.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        path: 'admin/business/:businessId/3d-overview',
        name: 'admin.business.3d',
        components: {
          default: () => import('pages/planner/3DoverviewPlanner.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        path: 'admin/business/:businessId/settings',
        name: 'admin.business.settings',
        components: {
          default: () => import('pages/shared/Settings.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        path: 'admin/business/:businessId/payments',
        name: 'admin.business.payments',
        components: {
          default: () => import('pages/shared/Payments.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        // "Collect Payments" — the business enrolling with Stripe / PayPal / Grow so it
        // can take money from ITS clients. Distinct from `admin.business.payments`
        // above, which is where the business buys credits from US.
        path: 'admin/business/:businessId/collect-payments',
        name: 'admin.business.collectpayments',
        components: {
          default: () => import('pages/shared/CollectPayments.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        // Gift cards — selling credit up front. Deliberately its OWN page rather than a
        // tab of Collect Payments: that page is enrollment, done once; this one is used
        // every week, by different people, and Admin will link straight to it.
        path: 'admin/business/:businessId/gift-cards',
        name: 'admin.business.giftcards',
        components: {
          default: () => import('pages/shared/GiftCards.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        path: 'admin/business/:businessId/payments/subscription',
        name: 'admin.business.subscription',
        components: {
          default: () => import('pages/shared/Subscription.vue'),
          header: () => import('layouts/AdminToolbar.vue'),
        }
      },
      {
        path: 'verification',
        name: 'verification',
        component: () => import('pages/shared/Verification.vue')
      },
      {
        path: 'login',
        name: 'login',
        // Sign-up / log-in A/B (src/utils/onboardingVariant.ts): the playground version for this
        // device, EXCEPT links that belong to Login.vue — coworker invites (?tab=host|coworker), the
        // marketing sandbox (?demo=1), the flavor-lock notice (?flavorLocked=) and ?expired=1.
        beforeEnter: (to) => {
          const q = to.query
          const classicOnly = q.tab === 'host' || q.tab === 'coworker' || q.demo !== undefined || q.flavorLocked !== undefined || q.expired !== undefined
          return !classicOnly && deviceVariant(q.flow) === 'playground' ? { name: 'login.playground', query: q } : true
        },
        components: {
          default: () => import('pages/shared/Login.vue'),
          header: () => import('layouts/BasicToolbar.vue'),
        }
      },
      {
        // The playground sign-up / log-in — reachable directly for testing.
        path: 'login/new',
        name: 'login.playground',
        meta: { languageInBar: true },   // BasicToolbar shows the language pill here
        components: {
          default: () => import('pages/shared/LoginPlayground.vue'),
          header: () => import('layouts/BasicToolbar.vue'),
        }
      }
    ],
  },

  // Public payment link — where a "Request Payment" QR or WhatsApp message lands.
  //
  // ⚠️ TOP LEVEL AND UNGUARDED, on purpose. The person opening it is a guest paying a
  // bill, not a logged-in user, and the planner guard only gates paths containing
  // 'admin' — so this must never be nested under one. Registered in EVERY build because
  // the link is built from whichever planner host the staff member raised it on.
  {
    // `:outcome` = 'paid' | 'cancelled' — Grow's return URLs. Grow rejects a
    // successUrl with special characters, so `?paid=1` cannot come back from Grow.
    path: '/pay/:id/:outcome?',
    name: 'payment.request',
    component: () => import('pages/shared/PayRequestPage.vue'),
  },

  // The gift card itself, once bought: code, QR and live balance. Public and unauthenticated
  // for the same reason as /pay — it is opened from an email, a printout or a forwarded
  // message by someone who has no account, possibly months later.
  {
    path: '/gift/:id',
    name: 'gift.card',
    component: () => import('pages/shared/GiftCardPage.vue'),
  },

  // The shop BEFORE a card exists: a guest picks the amount themselves. This is the link a
  // business puts in its bio or on its own site, so it is keyed by business, not by card.
  {
    path: '/gift-shop/:businessId',
    name: 'gift.shop',
    component: () => import('pages/shared/GiftShopPage.vue'),
  },

  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    component: () => import('pages/ErrorNotFound.vue'),
  },
];

// ⭐ Guest store app (APP_MODE=guest, "ResTa Clients", 2026-10-17): diners only. The staff
// routes are NOT REGISTERED — not hidden, gone — so no button, deep link, expired-session
// bounce or typed URL can reach login/verification/admin. Anything unknown goes home.
if (IS_GUEST_APP) {
  const staffOnly = (path: string) => path === 'login' || path.startsWith('login/') || path === 'verification' || path.startsWith('admin');
  routes[0].children = routes[0].children!.filter((r) => !staffOnly(r.path));
  routes[routes.length - 1] = { path: '/:catchAll(.*)*', redirect: '/' };
  // Owner 2026-10-18: the Clients apps OPEN on "Where to book?" (the search) — the landing
  // page's only useful action there was "Book a …", one tap nobody needs. Home redirects.
  const home = routes[0].children!.findIndex((r) => r.name === 'home');
  routes[0].children![home] = { path: '', name: 'home', redirect: { name: 'select-business' } };
}

export default routes;
